<template>
  <div class="single pt-xxxl">
    <div class="single__heading wrapper mb-l">
      <div class="single__heading__headline mb-s category">
        {{ content.headline }}
      </div>
      <h1 class="single__heading__title h2" v-html="content.title"></h1>

      <time
        v-if="content.publicationDate"
        class="single__heading__date mt-s"
        :datetime="content.publicationDate"
      >
        {{ formatDate(content.publicationDate) }}
      </time>
    </div>

    <UiPicture
      v-if="content.picture"
      class="single__picture wrapper"
      :content="content.picture"
      :alt="content.picture.alt"
      :sets="
        content.header_type === 'normal'
          ? ['320', '480', '640', '960', '1280']
          : [
              '320',
              '480',
              '640',
              '960',
              '1280',
              '1440',
              '1600',
              '1920',
              '2240',
              '2560',
              '2880',
            ]
      "
      :sizes="
        content.header_type === 'normal'
          ? '(min-width: 1680px) 1280w, 100vw'
          : '100vw'
      "
    />

    <HtmlText
      v-if="content.htmltext"
      :content="content.htmltext"
      class="single__content wrapper m-xl"
    />

    <UiShare />

    <CtaRibbon
      v-if="content.picture_cta && content.picture_cta.title"
      :content="content.picture_cta"
    />

    <GridNews
      v-if="content.news_grid?.cards"
      :content="content.news_grid"
      class="single__news"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import CtaRibbon from '@/components/cta/Ribbon.vue'
import HtmlText from '@/components/global/HtmlText.vue'
import GridNews from '@/components/grid/News.vue'
import UiPicture from '@/components/ui/Picture.vue'
import UiShare from '@/components/ui/Share.vue'
import { useCountry } from '@/plugins/country'

import type { PropType } from 'vue'
import type { RibbonCta } from '@/components/cta/Ribbon.vue'
import type { NewsGrid } from '@/components/grid/News.vue'
import type { Picture } from '@/types'

interface Single {
  header_type: 'normal' | 'highlight'
  headline: string
  htmltext: string
  news_grid?: NewsGrid
  picture?: Picture
  picture_cta: RibbonCta
  publicationDate: string
  title: string
}

defineProps({
  content: {
    type: Object as PropType<Single>,
    required: true,
  },
})

const formatDate = (value: string) => {
  const date = new Date(parseInt(value, 10))
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  const { locale } = useI18n()
  const country = useCountry()

  return date.toLocaleDateString(`${locale.value}-${country}`, options)
}
</script>

<style lang="scss" scoped>
.single {
  :deep(.news-grid__title) {
    @extend %section-title-spacing;
  }
}

.single__heading,
.single__picture.wrapper {
  @include mq(m) {
    --wrapper-padding: 2;
  }

  @include mq(wrapper) {
    --wrapper-padding: 4;
  }
}

.single__heading__date {
  display: block;
}

.single__picture {
  img {
    width: 100%;
    object-fit: cover;
    object-position: center;

    @include mq(m) {
      aspect-ratio: 16 / 7;
    }
  }

  &.wrapper {
    img {
      border-radius: 2rem;
    }
  }
}

.single__content {
  @include mq(m) {
    --wrapper-padding: 4;
  }

  @include mq(wrapper) {
    --wrapper-padding: 5;
  }
}

.single__news {
  background: $c-white;
}
</style>
